/**
 * `page_link` block (spec §7.4.14, M24): a card standing for another page of the workspace. It shows
 * the page's title, its folder path and its first lines; a click opens the page. It holds no content
 * of the target — a mirror does that (§7.4.13).
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FileText, FolderOpen } from "lucide-react";
import { useMemo, useState } from "react";
import { usePages, useTrash } from "../../api/hooks";
import { queryCacheKey, runWithCache } from "../../graph/queryCache";
import { useT } from "../../i18n";
import { docIri } from "../../lib/iri";
import { folderPath, folderPaths } from "../../pages/pageTree";
import { useDoc, useLoroSelector } from "../../sync/hooks";
import { Input } from "../../ui/Input";
import type { BlockComponentProps } from "../registry";
import { LoroTextInput, bodyMap } from "../textField";

const NO_PATH: string[] = [];

const PREVIEW_LINES = 3;

/** The target's first root blocks' labels, in order (spec §7.4.14); replayed from the cache offline. */
function usePreview(workspaceId: string | undefined, docId: string | null) {
  return useQuery({
    queryKey: ["pageLinkPreview", workspaceId, docId],
    enabled: !!workspaceId && !!docId,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const q = `PREFIX km: <https://w3id.org/kss/ns#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
SELECT ?l WHERE { GRAPH ?g { ?b km:inDocument <${docIri(docId!)}> ; km:position ?p ; rdfs:label ?l } FILTER NOT EXISTS { GRAPH ?g2 { ?b km:parentBlock ?x } } }
ORDER BY ?p LIMIT ${PREVIEW_LINES}`;
      const run = await runWithCache(workspaceId!, q, await queryCacheKey("page-link", docId!, q));
      const rows = (run.result.results as { results?: { bindings?: Array<Record<string, { value: string } | undefined>> } }).results?.bindings ?? [];
      return rows.map((r) => r.l?.value ?? "").filter(Boolean);
    },
  });
}

export function PageLinkBlock({ handle, node, readOnly, workspaceId, onFocus }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const target = useLoroSelector(handle.doc, () => {
    const v = body?.get("target") as { docId?: string } | null | undefined;
    return typeof v?.docId === "string" ? v.docId : null;
  });
  const [choosing, setChoosing] = useState(false);
  const pages = usePages(workspaceId ?? "");
  const trash = useTrash(workspaceId ?? "", !!workspaceId && !!target);
  const wsHandle = useDoc("workspace", workspaceId ?? "");
  // every page's path, then this one: a selector over the workspace document that read `target` (from
  // the page's own document) kept the old target's path until the workspace document changed
  const paths = useLoroSelector(wsHandle?.doc ?? null, folderPaths, (a, b) => JSON.stringify(a) === JSON.stringify(b));
  const path = (target && paths?.[target]) || NO_PATH;
  const preview = usePreview(workspaceId, target);
  if (!body) return null;

  const page = target ? pages.data?.find((p) => p.id === target) : undefined;
  const trashed = target && !page ? trash.data?.find((p) => p.id === target) : undefined;
  const choose = (docId: string) => {
    body.set("target", { docId });
    handle.doc.commit();
    setChoosing(false);
  };

  return (
    <div className="rounded-md border border-neutral-200 bg-[var(--surface)] p-2.5 shadow-[var(--shadow-sm)] dark:border-neutral-800" data-testid="page-link-block" data-target={target ?? ""} onFocus={onFocus}>
      {target && !choosing ? (
        <div className="flex items-start gap-2">
          <FileText size={16} className="mt-0.5 shrink-0 text-neutral-500" aria-hidden="true" />
          <div className="min-w-0 grow">
            {path.length > 0 && (
              <div className="flex items-center gap-1 truncate text-xs text-neutral-500" data-testid="page-link-path">
                <FolderOpen size={12} aria-hidden="true" /> {path.join(" / ")}
              </div>
            )}
            {page && workspaceId ? (
              <Link to="/w/$workspaceId/d/$docId" params={{ workspaceId, docId: target }} className="font-medium text-accent hover:underline" data-testid="page-link-open">
                {page.title || t("page.untitled")}
              </Link>
            ) : (
              <span className="text-sm text-neutral-500" data-testid="page-link-state" data-state={trashed ? "trash" : pages.isError ? "offline" : "missing"}>
                {trashed ? t("pageLink.trashed", { title: trashed.title || t("page.untitled") }) : pages.isLoading ? t("common.loading") : t("pageLink.missing")}
              </span>
            )}
            {page && (preview.data?.length ?? 0) > 0 && (
              <div className="mt-1 space-y-0.5 text-xs text-neutral-500" data-testid="page-link-preview">
                {preview.data!.map((line, i) => (
                  <div key={i} className="truncate">
                    {line}
                  </div>
                ))}
              </div>
            )}
          </div>
          {!readOnly && (
            <button className="shrink-0 text-xs text-neutral-500 underline hover:text-neutral-900 dark:hover:text-neutral-100" onClick={() => setChoosing(true)} data-testid="page-link-change">
              {t("pageLink.change")}
            </button>
          )}
        </div>
      ) : readOnly ? (
        <span className="text-sm text-neutral-500">{t("pageLink.none")}</span>
      ) : (
        <PagePicker workspaceId={workspaceId ?? ""} exclude={handle.crdtDocId} onPick={choose} onCancel={target ? () => setChoosing(false) : undefined} />
      )}
      <LoroTextInput handle={handle} map={body} field="caption" placeholder={readOnly ? "" : t("media.caption")} readOnly={readOnly} testId="page-link-caption" />
    </div>
  );
}

/** A search over the workspace's pages, with each page's folder path (spec §8.25). */
export function PagePicker({ workspaceId, exclude, onPick, onCancel }: { workspaceId: string; exclude?: string; onPick(docId: string): void; onCancel?: () => void }) {
  const t = useT();
  const [q, setQ] = useState("");
  const pages = usePages(workspaceId);
  const wsHandle = useDoc("workspace", workspaceId);
  const needle = q.trim().toLowerCase();
  const hits = useMemo(
    () => (pages.data ?? []).filter((p) => p.id !== exclude && (!needle || (p.title || "").toLowerCase().includes(needle))).slice(0, 30),
    [pages.data, exclude, needle],
  );
  return (
    <div className="space-y-1.5" data-testid="page-picker">
      <div className="flex items-center gap-2">
        <Input autoFocus placeholder={t("pageLink.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="page-picker-search" />
        {onCancel && (
          <button className="shrink-0 text-xs text-neutral-500 underline" onClick={onCancel}>
            {t("common.cancel")}
          </button>
        )}
      </div>
      <ul className="max-h-56 overflow-auto rounded border border-neutral-200 text-sm dark:border-neutral-800">
        {hits.map((p) => {
          const path = wsHandle ? folderPath(wsHandle.doc, p.id) : [];
          return (
            <li key={p.id}>
              <button className="block w-full px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={() => onPick(p.id)} data-testid="page-picker-option" data-doc-id={p.id}>
                <span className="font-medium">{p.title || t("page.untitled")}</span>
                {path.length > 0 && <span className="ml-2 text-xs text-neutral-500">{path.join(" / ")}</span>}
              </button>
            </li>
          );
        })}
        {hits.length === 0 && <li className="px-2 py-1.5 text-neutral-500">{pages.isError ? t("pageLink.offline") : t("pageLink.noMatch")}</li>}
      </ul>
    </div>
  );
}
